import { useEffect, useState } from "react";
import { agentsApi, alertsApi, huddleApi } from "@/lib/api-client";
import { AGENT_META, SEVERITY_COLOR, relativeTime, fmtUSDFull } from "@/lib/format";
import { Radar, ShieldAlert, Zap, Loader2, CheckCircle2, XCircle, Play, ChevronRight, ScanSearch, Ban, Landmark, PenTool, Coins, CreditCard, Users } from "lucide-react";
import { toast } from "sonner";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { paymentsApi } from "@/lib/api-client";

const AGENT_ICONS = { scout: Radar, sentinel: ShieldAlert, executor: Zap, auditor: ScanSearch, strategist: Landmark, creator: PenTool, controller: Coins };

export default function AgentBoardroom({ user, onAction, refreshKey }) {
  const [insights, setInsights] = useState([]);
  const [loading, setLoading] = useState(true);
  const [runningAgent, setRunningAgent] = useState(null);
  const [tab, setTab] = useState("all");

  const load = async () => {
    try {
      const items = await agentsApi.list();
      setInsights(items);
    } catch { toast.error("Failed to load agents"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [refreshKey]);

  const run = async (agent) => {
    setRunningAgent(agent);
    try {
      const r = await agentsApi.run(agent, "");
      toast.success(`${AGENT_META[agent].label} deployed · ${r.created.length} new insights`);
      await load();
    } catch (e) {
      toast.error(`Agent failed: ${e?.response?.data?.detail || "unknown"}`);
    } finally { setRunningAgent(null); }
  };

  const act = async (insight, action) => {
    try {
      await agentsApi.act(insight.id, action);
      toast.success(`Marked ${action}`);
      await load();
      onAction?.();
    } catch (e) {
      const code = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (code === 423) toast.error(detail || "Guard closed");
      else if (code === 409 && String(detail || "").includes("Financial")) toast.error("Financial insight — use Approve & Charge");
      else toast.error("Action failed");
    }
  };

  const chargeInsight = async (insight, payment_method_id) => {
    try {
      const r = await paymentsApi.approveCharge(insight.id, payment_method_id);
      if (r.paid) toast.success(`Charged ${fmtUSDFull(r.amount)} · Executed`);
      else toast.warning(`Charge status: ${r.status}`);
      await load();
      onAction?.();
    } catch (e) {
      const code = e?.response?.status;
      const detail = e?.response?.data?.detail || "Charge failed";
      if (code === 423) toast.error(detail);
      else if (code === 402) toast.error(`Declined: ${detail}`);
      else toast.error(detail);
    }
  };

  const filtered = tab === "all" ? insights : insights.filter((i) => i.agent === tab);

  return (
    <section className="uc-card p-5 uc-fade flex flex-col h-[720px] min-h-0" data-testid="agent-boardroom">      <div className="mb-3">
        <div className="uc-chip mb-2"><span className="uc-dot bg-amber-400"></span> AGENT BOARDROOM</div>
        <h2 className="font-serif-display text-xl">Proactive Intelligence</h2>
      </div>

      {/* Agent triggers — compact so briefings get the room */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-1.5 mb-3">
        {Object.entries(AGENT_META).map(([k, meta]) => {
          const Icon = AGENT_ICONS[k];
          return (
            <button
              key={k}
              data-testid={`run-agent-${k}`}
              onClick={() => run(k)}
              disabled={runningAgent === k}
              className="px-2 py-1.5 border border-white/10 hover:border-white/25 transition-colors text-left uc-focus disabled:opacity-50"
              style={{ background: "rgba(255,255,255,0.02)" }}
            >
              <div className="flex items-center gap-1.5">
                <Icon size={12} style={{ color: meta.color }} />
                <span className="font-serif-display text-[13px]">{meta.label}</span>
                {runningAgent === k && <Loader2 size={10} className="animate-spin text-white/50" />}
              </div>
              <div className="font-mono-uc text-[8px] uppercase tracking-wider text-white/35 mt-0.5">{meta.role}</div>
            </button>
          );
        })}
      </div>

      {/* Tabs — single scrollable row */}
      <Tabs value={tab} onValueChange={setTab} className="flex-1 flex flex-col min-h-0">
        <TabsList className="bg-transparent border-b border-white/10 rounded-none p-0 h-auto justify-start gap-2.5 mb-3 flex-nowrap overflow-x-auto uc-scroll w-full">
          {[["all","All"],["scout","Scout"],["sentinel","Sentinel"],["executor","Executor"],["auditor","Auditor"],["strategist","Strategist"],["creator","Creator"],["controller","Controller"]].map(([k, label]) => (
            <TabsTrigger
              key={k}
              value={k}
              data-testid={`agent-tab-${k}`}
              className="rounded-none border-b-2 border-transparent data-[state=active]:border-amber-400 data-[state=active]:bg-transparent data-[state=active]:text-white text-white/50 px-0 pb-2 font-mono-uc text-[10px] uppercase tracking-widest flex-none"
            >
              {label}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value={tab} className="flex-1 overflow-y-auto uc-scroll -mr-2 pr-2 mt-0">
          <div className="space-y-2">
            {filtered.map((i) => (
              <InsightItem key={i.id} insight={i} onAct={act} onCharge={chargeInsight} user={user} />
            ))}
            {!loading && filtered.length === 0 && (
              <div className="text-white/40 text-sm py-8 text-center italic">
                No insights yet. Deploy an agent above.
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </section>
  );
}

function InsightItem({ insight, onAct, onCharge, user }) {
  const meta = AGENT_META[insight.agent];
  const Icon = AGENT_ICONS[insight.agent];
  const sev = SEVERITY_COLOR[insight.severity] || "#71717A";
  const budget = user?.autopilot_budget || 100;
  const overBudget = insight.agent === "executor" && insight.estimated_cost > budget;
  const isFinancial = ["executor", "controller"].includes(insight.agent) && Number(insight.estimated_cost) > 0;
  const [huddling, setHuddling] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [methods, setMethods] = useState([]);
  const [charging, setCharging] = useState(false);

  const openPicker = async () => {
    setPickerOpen(true);
    try { setMethods(await paymentsApi.listMethods()); }
    catch { toast.error("Could not load payment methods"); }
  };

  const doCharge = async (pmId) => {
    setCharging(true);
    await onCharge(insight, pmId);
    setCharging(false);
    setPickerOpen(false);
  };

  return (
    <div
      className="p-3 border border-white/5 hover:border-white/15 transition-colors"
      style={{ borderLeft: `2px solid ${meta.color}` }}
      data-testid={`insight-${insight.id}`}
    >
      <div className="flex items-start gap-2 mb-1">
        <Icon size={13} style={{ color: meta.color }} className="mt-0.5 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <span className="text-sm text-white font-medium">{insight.title}</span>
            <span className="font-mono-uc text-[9px] uppercase tracking-wider" style={{ color: sev }}>{insight.severity}</span>
          </div>
          <p className="text-xs text-white/60 mt-1 leading-relaxed">{insight.message}</p>

          <div className="flex items-center justify-between mt-2 flex-wrap gap-2">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-mono-uc text-[10px] text-white/40">{relativeTime(insight.created_at)}</span>
              {insight.estimated_cost > 0 && (
                <span className={`font-mono-uc text-[10px] px-1.5 py-0.5 border ${overBudget ? "border-red-400/40 text-red-400" : "border-white/10 text-white/70"}`}>
                  {fmtUSDFull(insight.estimated_cost)}
                </span>
              )}
              {overBudget && (
                <span className="font-mono-uc text-[9px] uppercase tracking-wider text-red-400">Over Auto-Pilot</span>
              )}
              {insight.blocked_by_audit && (
                <span className="font-mono-uc text-[9px] uppercase tracking-wider text-red-400 flex items-center gap-1">
                  <Ban size={9}/> Audit Gate
                </span>
              )}
              {insight.blocked_by_controller && (
                <span className="font-mono-uc text-[9px] uppercase tracking-wider text-teal-400 flex items-center gap-1">
                  <Ban size={9}/> Controller Hold
                </span>
              )}
              <StatusBadge status={insight.status} />
            </div>
            {insight.status === "pending" && (
              <div className="flex gap-1 flex-wrap items-center">
                <button
                  data-testid={`huddle-${insight.id}`}
                  onClick={async () => {
                    setHuddling(true);
                    try {
                      const h = await huddleApi.run(insight.id);
                      toast.success(`Huddle synthesized — ${h.dissents?.length || 0} dissent(s) · impact $${Math.round(h.impact?.usd || 0).toLocaleString()} · sign-off waiting in Action Hub`);
                    } catch (e) {
                      toast.error(e?.response?.data?.detail || "Huddle failed");
                    } finally { setHuddling(false); }
                  }}
                  disabled={huddling}
                  className="text-[10px] font-mono-uc uppercase tracking-wider px-2 py-1 border border-amber-500/40 text-amber-400 hover:bg-amber-500/10 disabled:opacity-40 transition-colors flex items-center gap-1"
                  title="Convene all 7 agents for parallel peer review"
                >
                  {huddling ? <Loader2 size={9} className="animate-spin"/> : <Users size={9}/>} Huddle
                </button>
                {isFinancial ? (
                  <button
                    data-testid={`approve-charge-${insight.id}`}
                    onClick={openPicker}
                    disabled={insight.blocked_by_audit || insight.blocked_by_controller}
                    className="text-[10px] font-mono-uc uppercase tracking-wider px-2 py-1 border border-teal-500/40 text-teal-400 hover:bg-teal-500/10 disabled:opacity-40 disabled:cursor-not-allowed transition-colors flex items-center gap-1"
                    title={insight.blocked_by_audit ? "Audit Gate closed" : insight.blocked_by_controller ? "Liquidity Guard tripped" : "Approve & Charge saved payment method"}
                  >
                    <CreditCard size={10}/> Approve & Charge {fmtUSDFull(insight.estimated_cost)}
                  </button>
                ) : (
                  insight.agent === "executor" && (
                    <button
                      data-testid={`execute-${insight.id}`}
                      onClick={() => onAct(insight, "execute")}
                      className="text-[10px] font-mono-uc uppercase tracking-wider px-2 py-1 border border-emerald-500/40 text-emerald-400 hover:bg-emerald-500/10 transition-colors flex items-center gap-1"
                    >
                      <Play size={9}/> Execute
                    </button>
                  )
                )}
                <button
                  data-testid={`approve-${insight.id}`}
                  onClick={() => onAct(insight, "approve")}
                  className="text-[10px] font-mono-uc uppercase tracking-wider px-2 py-1 border border-amber-500/40 text-amber-400 hover:bg-amber-500/10 transition-colors flex items-center gap-1"
                >
                  <CheckCircle2 size={9}/> Approve
                </button>
                <button
                  data-testid={`dismiss-${insight.id}`}
                  onClick={() => onAct(insight, "dismiss")}
                  className="text-[10px] font-mono-uc uppercase tracking-wider px-2 py-1 border border-white/15 text-white/50 hover:bg-white/5 transition-colors flex items-center gap-1"
                >
                  <XCircle size={9}/> Dismiss
                </button>
              </div>
            )}
          </div>
          {pickerOpen && (
            <div className="mt-2 p-2 border border-teal-500/30 bg-teal-500/5" data-testid={`payment-picker-${insight.id}`}>
              <div className="font-mono-uc text-[10px] uppercase tracking-widest text-teal-400 mb-2">
                Commander Approval · Select method
              </div>
              {methods.length === 0 ? (
                <div className="text-white/50 text-[11px]">No payment methods on file. Add one in Command Wallet.</div>
              ) : (
                <div className="space-y-1">
                  {methods.map((m) => (
                    <button
                      key={m.id}
                      data-testid={`charge-with-${m.id}`}
                      disabled={charging}
                      onClick={() => doCharge(m.id)}
                      className="w-full flex items-center justify-between p-2 border border-white/10 hover:border-teal-500/40 hover:bg-teal-500/10 transition-colors text-left disabled:opacity-40"
                    >
                      <span className="font-mono-uc text-xs">{m.label}</span>
                      <span className="font-mono-uc text-[10px] text-white/50">••{m.last4}</span>
                    </button>
                  ))}
                </div>
              )}
              <button onClick={() => setPickerOpen(false)} className="mt-2 font-mono-uc text-[10px] text-white/40 hover:text-white/70">
                Cancel
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    pending: { c: "#A1A1AA", l: "Pending" },
    approved: { c: "#F59E0B", l: "Approved" },
    executed: { c: "#22C55E", l: "Executed" },
    dismissed: { c: "#71717A", l: "Dismissed" },
  };
  const m = map[status] || map.pending;
  return (
    <span className="font-mono-uc text-[9px] uppercase tracking-wider flex items-center gap-1" style={{ color: m.c }}>
      <span className="uc-dot" style={{ background: m.c }}></span>{m.l}
    </span>
  );
}
